<template>
  <div class="card">
    <div class="card-header">
      <div class="card-title">
        <i class="fa-solid fa-sliders"></i>
        <h2>Team Preferences</h2>
      </div>
      <button class="btn-text" @click="$emit('edit')">
        <i class="fa-solid fa-pen"></i>
        Edit
      </button>
    </div>
    <div class="card-body">
      <div class="preferences-grid">
        <div class="preference-item">
          <div class="preference-label">
            <i class="fa-solid fa-bolt"></i>
            Skill Level
          </div>
          <div class="preference-value">
            <span class="skill-badge" :class="team.skill_level?.toLowerCase()">
              {{ team.skill_level || 'Not Set' }}
            </span>
          </div>
        </div>
        <div v-if="!hideAvailability" class="preference-item">
          <div class="preference-label">
            <i class="fa-solid fa-calendar"></i>
            Availability
          </div>
          <div class="preference-value">
            {{ team.availability || 'Not Set' }}
          </div>
        </div>
        <div class="preference-item">
          <div class="preference-label">
            <i class="fa-solid fa-flag"></i>
            Competition Level
          </div>
          <div class="preference-value">
            {{ capitalizeText(team.preferences?.competition_level) || 'Casual' }}
          </div>
        </div>
        <div class="preference-item">
          <div class="preference-label">
            <i class="fa-solid fa-clock"></i>
            Preferred Time
          </div>
          <div class="preference-value">
            {{ capitalizeText(team.preferences?.preferred_time) || 'Evenings' }}
          </div>
        </div>
        <div class="preference-item preference-item-days" v-if="team.preferences?.preferred_days && team.preferences.preferred_days.length > 0">
          <div class="preference-label">
            <i class="fa-solid fa-calendar-days"></i>
            Preferred Days
          </div>
          <div class="preference-value">
            <span class="preferred-days-list">
              <span 
                v-for="(day, index) in formatPreferredDays(team.preferences.preferred_days)" 
                :key="index"
                class="day-badge"
              >
                {{ day }}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TeamPreferences',
  props: {
    team: {
      type: Object,
      required: true
    },
    formatPreferredDays: {
      type: Function,
      required: true
    },
    capitalizeText: {
      type: Function,
      required: true
    },
    hideAvailability: {
      type: Boolean,
      default: false
    }
  },
  emits: ['edit']
};
</script>

<style scoped>
.card {
  background: var(--card-bg);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 600px) {
  .card-header {
    padding: 14px 16px;
  }
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title i {
  font-size: 1.1rem;
  color: var(--section-icon);
}

.card-title h2 {
  margin: 0;
  color: var(--heading-color);
  font-size: 1.1rem;
  font-weight: 600;
}

.btn-text {
  background: none;
  border: none;
  color: var(--section-icon);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-text:hover {
  color: var(--heading-color);
  transform: translateX(5px);
}

.card-body {
  padding: 16px 20px 20px;
}

@media (max-width: 600px) {
  .card-body {
    padding: 14px 16px;
  }
}

.preferences-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px 28px;
}

@media (min-width: 900px) {
  .preferences-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 22px 32px;
  }
}

@media (max-width: 600px) {
  .preferences-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}

.preference-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-height: auto;
}

.preference-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--subtext-color);
  font-weight: 500;
  font-size: 0.8rem;
  min-width: 0;
}

.preference-label i {
  flex-shrink: 0;
  opacity: 0.9;
  font-size: 0.85rem;
}

.preference-value {
  color: var(--heading-color);
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.3;
  word-break: break-word;
}

.preference-item-days {
  padding: 8px 10px;
  gap: 4px;
}

.preference-item-days .preference-label {
  font-size: 0.7rem;
  gap: 4px;
}

.preference-item-days .preference-label i {
  font-size: 0.7rem;
}

.preference-item-days .preference-value {
  font-size: 0.75rem;
}

.preferred-days-list {
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  overflow-x: auto;
  width: 100%;
}

.preferred-days-list::-webkit-scrollbar {
  height: 2px;
}

.day-badge {
  display: inline-block;
  padding: 2px 6px;
  background: linear-gradient(135deg, rgba(58, 134, 255, 0.15), rgba(58, 134, 255, 0.05));
  border: 1px solid rgba(58, 134, 255, 0.3);
  border-radius: 8px;
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--border-color-hover);
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.day-badge:hover {
  background: linear-gradient(135deg, rgba(58, 134, 255, 0.25), rgba(58, 134, 255, 0.15));
  border-color: var(--border-color-hover);
  transform: translateY(-1px);
}

.skill-badge {
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-block;
}

.skill-badge.beginner {
  background: var(--pending-bg);
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}

.skill-badge.intermediate {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.skill-badge.advanced {
  background: linear-gradient(135deg, #9C27B0, #7B1FA2);
  color: white;
}
</style>
